class R13CheckoutPlus extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;

    this.variantId = Number(this.dataset.variantId);
    this.lineKey = this.dataset.lineKey || '';
    this.toggle = this.querySelector('[data-r13-checkout-plus-toggle]');
    this.spinner = this.toggle?.querySelector('.loading__spinner');
    this.error = this.querySelector('[data-r13-checkout-plus-error]');
    this.modal = this.querySelector('[data-r13-checkout-plus-modal]');
    this.cart = document.querySelector('cart-drawer');

    this.toggle?.addEventListener('click', () => this.toggleCheckoutPlus());
    this.toggle?.addEventListener('keydown', (event) => {
      if (event.code === 'Enter' || event.code === 'Space') {
        event.preventDefault();
        this.toggleCheckoutPlus();
      }
    });
    this.querySelector('[data-r13-checkout-plus-info]')?.addEventListener('click', (event) => {
      event.stopPropagation();
      this.openModal();
    });
    this.querySelectorAll('[data-r13-checkout-plus-close]').forEach((button) => {
      button.addEventListener('click', () => this.closeModal());
    });
    this.addEventListener('keyup', (event) => {
      if (event.code === 'Escape') this.closeModal();
    });

    this.autoAddIfPreselected();
  }

  get isAdded() {
    return this.classList.contains('is-added') && this.lineKey;
  }

  async toggleCheckoutPlus() {
    if (!this.variantId || !this.toggle) return;
    if (this.toggle.getAttribute('aria-disabled') === 'true') return;

    this.setLoading(true);
    this.showError('');

    try {
      const sections = this.cart?.getSectionsToRender?.().map((section) => section.id) || [];
      const url = this.isAdded ? (window.routes?.cart_change_url || '/cart/change.js') : (window.routes?.cart_add_url || '/cart/add.js');
      if (this.isAdded) {
        window.sessionStorage?.setItem('r13CheckoutPlusOptOut', 'true');
      } else {
        window.sessionStorage?.removeItem('r13CheckoutPlusOptOut');
      }
      const body = this.isAdded
        ? { id: this.lineKey, quantity: 0, sections, sections_url: window.location.pathname }
        : { id: this.variantId, quantity: 1, sections, sections_url: window.location.pathname };

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: JSON.stringify(body),
      });
      const data = await response.json();
      if (!response.ok || data.status) throw new Error(data.description || data.message || 'Unable to update Checkout+.');

      if (window.PUB_SUB_EVENTS && typeof window.publish === 'function') {
        window.publish(window.PUB_SUB_EVENTS.cartUpdate, {
          source: 'r13-checkout-plus',
          productVariantId: this.variantId,
          cartData: data,
        });
      }

      this.cart?.renderContents?.(data);
    } catch (error) {
      this.showError(error.message || 'Unable to update Checkout+. Please try again.');
      this.setLoading(false);
    }
  }

  setLoading(loading) {
    if (!this.toggle) return;
    this.toggle.classList.toggle('loading', loading);
    this.toggle.setAttribute('aria-disabled', loading ? 'true' : 'false');
    this.spinner?.classList.toggle('hidden', !loading);
  }

  autoAddIfPreselected() {
    if (this.isAdded || !this.variantId) return;
    if (window.sessionStorage?.getItem('r13CheckoutPlusOptOut') === 'true') return;

    window.setTimeout(() => {
      if (!this.isConnected || this.isAdded) return;
      this.addCheckoutPlus();
    }, 50);
  }

  async addCheckoutPlus() {
    if (!this.variantId || !this.toggle) return;

    this.setLoading(true);
    this.showError('');

    try {
      const sections = this.cart?.getSectionsToRender?.().map((section) => section.id) || [];
      const response = await fetch(window.routes?.cart_add_url || '/cart/add.js', {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: JSON.stringify({
          id: this.variantId,
          quantity: 1,
          sections,
          sections_url: window.location.pathname,
        }),
      });
      const data = await response.json();
      if (!response.ok || data.status) throw new Error(data.description || data.message || 'Unable to add Checkout+.');

      if (window.PUB_SUB_EVENTS && typeof window.publish === 'function') {
        window.publish(window.PUB_SUB_EVENTS.cartUpdate, {
          source: 'r13-checkout-plus-auto',
          productVariantId: this.variantId,
          cartData: data,
        });
      }

      this.cart?.renderContents?.(data);
    } catch (error) {
      this.showError(error.message || 'Unable to add Checkout+. Please try again.');
      this.setLoading(false);
    }
  }

  showError(message) {
    if (!this.error) return;
    this.error.hidden = !message;
    this.error.textContent = message;
  }

  openModal() {
    if (!this.modal) return;
    this.modal.hidden = false;
    document.body.classList.add('overflow-hidden');
    this.querySelector('.r13-checkout-plus__close')?.focus();
  }

  closeModal() {
    if (!this.modal) return;
    this.modal.hidden = true;
    document.body.classList.remove('overflow-hidden');
    this.querySelector('[data-r13-checkout-plus-info]')?.focus();
  }
}

if (!customElements.get('r13-checkout-plus')) {
  customElements.define('r13-checkout-plus', R13CheckoutPlus);
}

if (!window.r13CheckoutPlusCleanupInitialized) {
  window.r13CheckoutPlusCleanupInitialized = true;

  const cleanupOrphanedCheckoutPlus = async () => {
    try {
      const productHandle = 'checkout-free-returns';
      const cartResponse = await fetch(window.routes?.cart_url ? `${window.routes.cart_url}.js` : '/cart.js', {
        headers: { Accept: 'application/json' },
      });
      const cart = await cartResponse.json();
      if (!cart.items?.length) return;

      const checkoutPlusItems = cart.items.filter((item) => item.handle === productHandle);
      if (!checkoutPlusItems.length || checkoutPlusItems.length !== cart.items.length) return;

      const updates = Object.fromEntries(checkoutPlusItems.map((item) => [item.key, 0]));
      const drawer = document.querySelector('cart-drawer');
      const sections = drawer?.getSectionsToRender?.().map((section) => section.id) || [];
      const updateResponse = await fetch(window.routes?.cart_update_url || '/cart/update.js', {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: JSON.stringify({ updates, sections, sections_url: window.location.pathname }),
      });
      const updateData = await updateResponse.json();
      if (updateResponse.ok && !updateData.status) drawer?.renderContents?.(updateData);
    } catch (error) {
      console.warn('Unable to clean up orphaned Checkout+ item', error);
    }
  };

  document.addEventListener('DOMContentLoaded', cleanupOrphanedCheckoutPlus);
  if (window.PUB_SUB_EVENTS && typeof window.subscribe === 'function') {
    window.subscribe(window.PUB_SUB_EVENTS.cartUpdate, () => window.setTimeout(cleanupOrphanedCheckoutPlus, 250));
  }
}
